import React, { useState } from 'react';

const plans = {
  e: { mo:349, an:279, inc:50,  ov:8 },
  s: { mo:499, an:399, inc:200, ov:6 },
};

const CheckIcon  = () => <i className="ti ti-check" style={{fontSize:15,color:'var(--success)',flexShrink:0,marginTop:1}} aria-hidden="true"/>;
const CrossIcon  = () => <i className="ti ti-x"     style={{fontSize:15,color:'var(--muted)',  flexShrink:0,marginTop:1}} aria-hidden="true"/>;

const FeatLine = ({ yes, children }) => (
  <li style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:13,color:'var(--text2)',lineHeight:1.45}}>
    {yes ? <CheckIcon/> : <CrossIcon/>}
    <span>{children}</span>
  </li>
);

const Bold = ({ children }) => (
  <strong style={{color:'var(--text)',fontWeight:600}}>{children}</strong>
);

export default function PricingPage() {
  const [annual,   setAnnual]   = useState(false);
  const [calcPlan, setCalcPlan] = useState('s');
  const [calcBill, setCalcBill] = useState('mo');
  const [calcQty,  setCalcQty]  = useState(200);

  // Overage calculation
  const p      = plans[calcPlan];
  const base   = calcBill === 'an' ? p.an : p.mo;
  const extra  = Math.max(0, calcQty - p.inc);
  const over   = Math.ceil(extra / 10) * p.ov;
  const total  = base + over;
  const overMsg = extra === 0
    ? `${p.inc} quotations included — no top-up needed`
    : `$${base} subscription + $${over} top-up (${extra} extra quotations)`;

  const Card = ({ tier, name, desc, monthlyPrice, annualPrice, annualBilled, children, featured, enterprise }) => (
    <div style={{
      background:'var(--surface)',
      border: featured ? '2px solid var(--precision)' : '1px solid var(--border)',
      borderRadius:14,
      padding:'1.5rem',
      display:'flex',flexDirection:'column',
      position:'relative',
    }}>
      {featured && (
        <div style={{
          position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',
          background:'var(--precision)',color:'#fff',
          fontSize:11,fontWeight:600,padding:'3px 14px',borderRadius:20,whiteSpace:'nowrap',
        }}>Most popular</div>
      )}
      <div style={{fontSize:10.5,fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--muted)',marginBottom:5}}>{tier}</div>
      <div style={{fontSize:20,fontWeight:700,color:'var(--text)',marginBottom:4}}>{name}</div>
      <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.55,marginBottom:'1rem',minHeight:36}}>{desc}</div>

      {enterprise ? (
        <div style={{fontSize:26,fontWeight:700,color:'var(--text)',marginBottom:4,marginTop:2}}>Talk to us</div>
      ) : (
        <div style={{display:'flex',alignItems:'baseline',gap:4,marginBottom:3}}>
          <span style={{fontSize:17,color:'var(--text2)',alignSelf:'flex-start',marginTop:5}}>$</span>
          <span style={{fontSize:36,fontWeight:700,color:'var(--text)',lineHeight:1}}>{annual ? annualPrice : monthlyPrice}</span>
          <span style={{fontSize:13,color:'var(--muted)'}}>/month</span>
        </div>
      )}

      <div style={{fontSize:12,minHeight:18,marginBottom:'1rem',color: annual && !enterprise ? 'var(--success)' : 'var(--muted)'}}>
        {enterprise ? '\u00a0' : annual ? `Billed $${annualBilled}/year` : '\u00a0'}
      </div>

      <hr style={{border:'none',borderTop:'1px solid var(--border)',margin:'.85rem 0'}}/>
      <ul style={{listStyle:'none',padding:0,margin:'0 0 1.25rem',flex:1,display:'flex',flexDirection:'column',gap:8}}>
        {children}
      </ul>

      {enterprise ? (
        <button className="btn btn-outline" style={{width:'100%',justifyContent:'center'}}>
          <i className="ti ti-phone" aria-hidden="true"/> Contact sales
        </button>
      ) : featured ? (
        <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}>
          Get started
        </button>
      ) : (
        <button className="btn btn-outline" style={{width:'100%',justifyContent:'center'}}>
          Get started
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div style={{marginBottom:28}}>
        <h1 className="page-title">Pricing</h1>
        <p className="page-sub">Simple, transparent plans. Pay only for what you use.</p>
      </div>

      {/* Annual toggle */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginBottom:32}}>
        <span style={{fontSize:14,color: !annual ? 'var(--text)' : 'var(--text2)',fontWeight: !annual ? 500 : 400}}>Monthly</span>
        <div onClick={() => { setAnnual(a => !a); setCalcBill(b => b === 'an' ? 'mo' : 'an'); }}
          style={{width:44,height:24,borderRadius:12,background:'var(--precision)',cursor:'pointer',position:'relative',flexShrink:0}}>
          <div style={{width:18,height:18,borderRadius:'50%',background:'#fff',position:'absolute',top:3,left: annual ? 23 : 3,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
        </div>
        <span style={{fontSize:14,color: annual ? 'var(--text)' : 'var(--text2)',fontWeight: annual ? 500 : 400}}>
          Annual
          <span style={{background:'var(--success)',color:'#fff',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:20,marginLeft:6}}>Save 20%</span>
        </span>
      </div>

      {/* Plan cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16,marginBottom:28}}>

        <Card tier="Starter" name="Essential" desc="Small staffing firm quoting across a handful of countries."
          monthlyPrice={349} annualPrice={279} annualBilled={279*12}>
          <FeatLine yes><Bold>50</Bold> quotations per month</FeatLine>
          <FeatLine yes><Bold>3</Bold> user seats</FeatLine>
          <FeatLine yes><Bold>3</Bold> countries of your choice</FeatLine>
          <FeatLine yes><Bold>30-day</Bold> quote history</FeatLine>
          <FeatLine yes>Actual Tax engine</FeatLine>
          <FeatLine yes>PDF &amp; Excel export</FeatLine>
          <FeatLine>Country comparison grid</FeatLine>
          <FeatLine>Bulk &amp; comparison export</FeatLine>
        </Card>

        <Card tier="Professional" name="Standard" desc="Active staffing firm quoting at volume across all 8 countries."
          monthlyPrice={499} annualPrice={399} annualBilled={399*12} featured>
          <FeatLine yes><Bold>200</Bold> quotations per month</FeatLine>
          <FeatLine yes><Bold>10</Bold> user seats</FeatLine>
          <FeatLine yes><Bold>All 8</Bold> countries unlocked</FeatLine>
          <FeatLine yes><Bold>Unlimited</Bold> quote history</FeatLine>
          <FeatLine yes>Actual Tax engine</FeatLine>
          <FeatLine yes>PDF &amp; Excel export</FeatLine>
          <FeatLine yes>Country comparison grid</FeatLine>
          <FeatLine yes>Bulk &amp; comparison export</FeatLine>
        </Card>

        <Card tier="Enterprise" name="Custom" desc="Large groups with specific requirements. Quoted by our sales team." enterprise>
          <FeatLine yes><Bold>Unlimited</Bold> quotations</FeatLine>
          <FeatLine yes><Bold>Unlimited</Bold> user seats</FeatLine>
          <FeatLine yes><Bold>All 8</Bold> countries + custom</FeatLine>
          <FeatLine yes><Bold>Unlimited</Bold> quote history</FeatLine>
          <FeatLine yes>Everything in Standard</FeatLine>
          <FeatLine yes>Multiple admin users</FeatLine>
          <FeatLine yes>White-label branded exports</FeatLine>
          <FeatLine yes>Dedicated account manager</FeatLine>
        </Card>

      </div>

      {/* Overage / top-up section */}
      <div className="card" style={{marginBottom:20}}>
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>
            <i className="ti ti-bolt" style={{marginRight:6,color:'var(--precision)'}} aria-hidden="true"/>
            Quotation top-ups — buy more when you need them
          </span>
        </div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text2)',marginBottom:14,lineHeight:1.6}}>
            When you reach your monthly quota, your account is paused until you purchase a top-up. Top-ups are charged immediately and credited to your account instantly — no waiting until month end.
          </p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12}}>
            {[
              { plan:'Essential', rate:'$8', per:'per 10 quotations', after:'after 50 included' },
              { plan:'Standard',  rate:'$6', per:'per 10 quotations', after:'after 200 included' },
              { plan:'Enterprise',rate:'None', per:'no top-up charges', after:'unlimited included' },
            ].map(o => (
              <div key={o.plan} style={{background:'var(--surface-1)',border:'1px solid var(--border)',borderRadius:8,padding:'.75rem 1rem'}}>
                <div style={{fontSize:10.5,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',marginBottom:5}}>{o.plan}</div>
                <div style={{fontSize:15,fontWeight:700,color:'var(--text)',marginBottom:2}}>{o.rate} <span style={{fontWeight:400,fontSize:13,color:'var(--text2)'}}>{o.per}</span></div>
                <div style={{fontSize:12,color:'var(--muted)'}}>{o.after}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cost calculator */}
      <div className="card" style={{marginBottom:20}}>
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>
            <i className="ti ti-calculator" style={{marginRight:6,color:'var(--precision)'}} aria-hidden="true"/>
            Estimate your monthly cost
          </span>
        </div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14,marginBottom:16}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Plan</label>
              <select className="form-control" value={calcPlan} onChange={e => setCalcPlan(e.target.value)}>
                <option value="e">Essential — $349/mo</option>
                <option value="s">Standard — $499/mo</option>
              </select>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Billing cycle</label>
              <select className="form-control" value={calcBill} onChange={e => setCalcBill(e.target.value)}>
                <option value="mo">Monthly</option>
                <option value="an">Annual (20% off)</option>
              </select>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Quotations / month</label>
              <input className="form-control" type="number" min={1} max={9999}
                value={calcQty} onChange={e => setCalcQty(Math.max(1, parseInt(e.target.value)||1))}/>
            </div>
          </div>

          <div style={{
            background:'var(--surface-1)',border:'1px solid var(--border)',
            borderRadius:10,padding:'1rem 1.25rem',
            display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,flexWrap:'wrap',
          }}>
            <div>
              <div style={{fontSize:12,color:'var(--muted)',marginBottom:4}}>Estimated monthly total</div>
              <div style={{fontSize:28,fontWeight:700,color:'var(--text)'}}>${total.toLocaleString()}</div>
              <div style={{fontSize:12,color:'var(--text2)',marginTop:4}}>{overMsg}</div>
            </div>
            {extra > 0 && (
              <div style={{
                background:'#FEF3C7',border:'1px solid #F59E0B',
                borderRadius:8,padding:'.65rem 1rem',maxWidth:260,
              }}>
                <div style={{fontSize:12,fontWeight:600,color:'#92400E',marginBottom:2}}>
                  <i className="ti ti-info-circle" style={{marginRight:4}} aria-hidden="true"/>
                  Top-up required
                </div>
                <div style={{fontSize:12,color:'#92400E',lineHeight:1.5}}>
                  {extra} extra quotations × top-up rate = <strong>${over}</strong> charged immediately when quota is reached.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="card">
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>Full plan comparison</span>
        </div>
        <div style={{overflowX:'auto'}}>
          <table className="table" style={{minWidth:520}}>
            <thead>
              <tr>
                <th style={{width:'40%'}}></th>
                <th>Essential</th>
                <th>Standard</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Monthly price','$349','$499','Custom'],
                ['Annual price','$279/mo','$399/mo','Custom'],
                ['Quotations included','50 / month','200 / month','Unlimited'],
                ['Top-up rate','$8 per 10','$6 per 10','None'],
                ['User seats','3','10','Unlimited'],
                ['Countries','3 of 8','All 8','All 8 + custom'],
                ['Quote history','30 days','Unlimited','Unlimited'],
              ].map(([label,...vals]) => (
                <tr key={label}>
                  <td style={{fontWeight:500}}>{label}</td>
                  {vals.map((v,i) => <td key={i}>{v}</td>)}
                </tr>
              ))}
              {[
                ['Actual Tax engine',     true,  true,  true ],
                ['PDF & Excel export',    true,  true,  true ],
                ['Country comparison',    false, true,  true ],
                ['Bulk & comparison export', false, true, true],
                ['White-label exports',   false, false, true ],
                ['Dedicated account manager', false, false, true],
              ].map(([label,...vals]) => (
                <tr key={label}>
                  <td style={{fontWeight:500}}>{label}</td>
                  {vals.map((v,i) => (
                    <td key={i}>
                      {v
                        ? <i className="ti ti-check" style={{color:'var(--success)',fontSize:15}} aria-hidden="true"/>
                        : <i className="ti ti-x"     style={{color:'var(--muted)',  fontSize:15}} aria-hidden="true"/>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}